import { useUserProfileApi } from '@/api/users';
import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { consultantPaths } from '@/paths';
import { useConsultantOrgService } from '@/services/consultant';
import { ArrowLeft, Building2, LayoutGrid, Shield } from 'lucide-react';
import { createContext, useContext, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';

interface ConsultantContext {
  orgId: string;
  orgName: string;
}

const ConsultantCtx = createContext<ConsultantContext>({
  orgId: '',
  orgName: 'Consultant',
});

export const useConsultantContext = () => useContext(ConsultantCtx);

export function ConsultantLayout() {
  const { data: user, isLoading: userLoading } = useUserProfileApi();
  const { orgId } = useParams<{ orgId: string }>();
  const { org, isLoading: orgLoading } = useConsultantOrgService(orgId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userLoading && user?.type !== 'consultant' && user?.type !== 'ditto') {
      navigate('/', { replace: true });
    }
  }, [userLoading, user?.type, navigate]);

  if (
    userLoading ||
    orgLoading ||
    (user?.type !== 'consultant' && user?.type !== 'ditto')
  ) {
    return null;
  }

  if (!org) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-neutral-900">404</h1>
          <p className="text-neutral-500">
            This consultant organization was not found.
          </p>
          <NavLink
            to="/"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"
          >
            <ArrowLeft className="size-4" />
            Back to app
          </NavLink>
        </div>
      </div>
    );
  }

  const orgName = org?.name ?? 'Consultant';

  const navItems = [
    {
      name: 'Regulatory updates',
      href: consultantPaths.updates,
      icon: Shield,
    },
    {
      name: 'Client updates triage',
      href: consultantPaths.triage,
      icon: LayoutGrid,
    },
    {
      name: 'Clients',
      href: consultantPaths.clientOrganisations,
      icon: Building2,
    },
  ];

  return (
    <ConsultantCtx.Provider value={{ orgId: orgId ?? '', orgName }}>
      <div className="flex h-screen bg-background">
        <aside className="w-64 shrink-0 border-r bg-white flex flex-col">
          <div className="px-4 py-4 border-b">
            <NavLink
              to="/"
              className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"
            >
              <ArrowLeft className="size-4" />
              Go to app
            </NavLink>
          </div>
          <div className="px-4 py-6">
            <h2 className="text-lg font-semibold">{orgName} Portal</h2>
          </div>
          <nav className="flex-1 px-3 space-y-1">
            {navItems.map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-neutral-100 text-neutral-900'
                      : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900'
                  )
                }
              >
                <item.icon className="size-4" />
                {item.name}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 flex flex-col min-h-0 min-w-0">
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <Outlet />
          </div>
          <Toaster />
        </main>
      </div>
    </ConsultantCtx.Provider>
  );
}
